import {
  DEFAULT_INTERACT_RADIUS,
  zoneAt,
  type MapData,
  type MapObject,
  type Zone,
} from '@etown/shared';
import { HINT_CHECK_HZ } from '../lib/constants';
import { distanceToRect, objectInCrosshair } from './aiming';
import { stuhlInReichweite, type Sitzplatz } from './sitzen';

const CHECK_INTERVAL_MS = 1000 / HINT_CHECK_HZ;

export interface ProximityHintEvents {
  onNearbyObject(obj: MapObject | null): void;
  /** Stuhl, auf den man sich setzen koennte (null = keiner in Reichweite). */
  onNearbyChair(platz: Sitzplatz | null): void;
  onZoneChange(zone: Zone | null): void;
  onPeerDistance(peerId: string, distanceTiles: number, peer: { x: number; y: number }): void;
}

/**
 * Rein lokale UX-Proximity (NICHT autoritativ): Interaktionshinweise,
 * Zonen-Anzeige und Distanzen fuer den Spatial-Audio-Gain. Die Entscheidung,
 * WER verbunden wird, trifft ausschliesslich der Server.
 */
export class ProximityHints {
  private timerMs = 0;
  private lastNearbyId: string | null = null;
  private lastChairKey: string | null = null;
  private lastZoneId: string | null = null;

  constructor(
    private map: MapData,
    private readonly events: ProximityHintEvents,
  ) {}

  setMap(map: MapData): void {
    this.map = map;
  }

  /**
   * `self.yaw` ist nur in der Ego-Ansicht gesetzt; dort waehlt der Blick das
   * Objekt aus, in der Draufsicht weiterhin die blosse Naehe.
   */
  update(
    dtMs: number,
    self: { x: number; y: number; yaw?: number | null },
    peerPositions: ReadonlyMap<string, { x: number; y: number }>,
    connectedPeerIds: readonly string[],
  ): void {
    this.timerMs += dtMs;
    if (this.timerMs < CHECK_INTERVAL_MS) return;
    this.timerMs = 0;

    // Interagierbares Objekt: im Fadenkreuz (Ego) oder in Reichweite (Draufsicht)
    let nearest: MapObject | null = null;
    if (self.yaw === null || self.yaw === undefined) {
      let nearestDist = Number.POSITIVE_INFINITY;
      for (const obj of this.map.objects) {
        const dist = distanceToRect(self.x, self.y, obj);
        const radius = obj.interactRadius || DEFAULT_INTERACT_RADIUS;
        if (dist <= radius && dist < nearestDist) {
          nearest = obj;
          nearestDist = dist;
        }
      }
    } else {
      nearest = objectInCrosshair(this.map.objects, self.x, self.y, self.yaw);
    }
    if ((nearest?.id ?? null) !== this.lastNearbyId) {
      this.lastNearbyId = nearest?.id ?? null;
      this.events.onNearbyObject(nearest);
    }

    // Stuhl in Reichweite — dieselbe Regel, nur auf der Dekor-Ebene
    const stuhl = stuhlInReichweite(this.map.layers.decor, self);
    const stuhlSchluessel = stuhl ? `${stuhl.x}:${stuhl.y}` : null;
    if (stuhlSchluessel !== this.lastChairKey) {
      this.lastChairKey = stuhlSchluessel;
      this.events.onNearbyChair(stuhl);
    }

    // Zone unter dem Avatar
    const zone = zoneAt(this.map.zones, Math.floor(self.x), Math.floor(self.y));
    if ((zone?.id ?? null) !== this.lastZoneId) {
      this.lastZoneId = zone?.id ?? null;
      this.events.onZoneChange(zone);
    }

    // Distanz zu verbundenen Peers (Audio-Lautstaerke)
    for (const peerId of connectedPeerIds) {
      const pos = peerPositions.get(peerId);
      if (!pos) continue;
      this.events.onPeerDistance(peerId, Math.hypot(pos.x - self.x, pos.y - self.y), pos);
    }
  }
}
